import React, { createContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useAppConfig } from './AppConfigContext';
import { APP_DEFAULTS } from '../config';

interface CountdownContextProps {
  targetDate: number;
  setTargetDate: (date: number) => void;
}

const fallbackTargetDate = Date.now() + 3 * 24 * 60 * 60 * 1000;

const CountdownContext = createContext<CountdownContextProps>({
  targetDate: fallbackTargetDate,
  setTargetDate: () => {},
});

interface CountdownProviderProps {
  children: ReactNode;
}

export const CountdownProvider: React.FC<CountdownProviderProps> = ({ children }) => {
  const { config } = useAppConfig();
  const [localOverrideDate, setLocalOverrideDate] = useState<number | null>(null);

  const remoteTargetDate =
    typeof config.countdownTargetDate === 'number'
      ? config.countdownTargetDate
      : typeof APP_DEFAULTS.countdownTargetDate === 'number'
        ? APP_DEFAULTS.countdownTargetDate
        : fallbackTargetDate;

  useEffect(() => {
    setLocalOverrideDate(null);
  }, [remoteTargetDate]);

  const targetDate = localOverrideDate ?? remoteTargetDate;

  useEffect(() => {
    console.log('[Countdown] targetDate resolved', {
      remoteTargetDate,
      remoteTargetDateHuman: remoteTargetDate ? new Date(remoteTargetDate).toLocaleString() : 'none',
      targetDate,
      targetDateHuman: targetDate ? new Date(targetDate).toLocaleString() : 'none',
      now: Date.now(),
      nowHuman: new Date().toLocaleString(),
      isFuture: targetDate > Date.now(),
    });
  }, [localOverrideDate, remoteTargetDate, targetDate]);

  const value = useMemo(
    () => ({
      targetDate,
      setTargetDate: setLocalOverrideDate,
    }),
    [targetDate]
  );

  return <CountdownContext.Provider value={value}>{children}</CountdownContext.Provider>;
};

export default CountdownContext;
